import CrudApiClient from './crudapiclient';
import { GATEWAY_API_URL } from '@admin-panel/config/env';
import { CrudFilters, CrudSort, MetaQuery } from '@refinedev/core';
import { IBankAccount, RefundMode } from '@admin-panel/types';
import { axiosInstance } from './axios';

type RefundActionMeta = MetaQuery & {
  refundRequestId?: number;
};

type UpdateRefundModePayload = {
  refundMode?: RefundMode;
  upiHandle?: string;
  bankAccount?: IBankAccount;
  comment: string;
};

type AdjustRefundAmountPayload = {
  refundAmount: number;
  comment: string;
  /** Temporary compatibility for the pre-history Fulfillment contract. */
  refundAmountAdjustmentReason?: string;
};

type ShelveRefundPayload = {
  comment: string;
  /** Temporary compatibility for the pre-history Fulfillment contract. */
  onholdShelfNote?: string;
};

type RecoverRefundPayload = {
  comment: string;
};

type RetryRefundPayload = {
  refundMode?: RefundMode;
  upiHandle?: string;
  bankAccount?: IBankAccount;
  retryReason?: string;
  cashPayoutConfirmed?: boolean;
  cashPayoutFacilityId?: number;
};

type ConvertStoreCreditRefundPayload = {
  refundMode: RefundMode.SOURCE | RefundMode.BANK_ACCOUNT | RefundMode.UPI;
  upiHandle?: string;
  bankAccount?: IBankAccount;
  reason: string;
  forceSourceOverride?: boolean;
  forceBankAccountOverride?: boolean;
  forceOverrideAcknowledged?: boolean;
  forceBankAccountAmount?: number;
  forceOverrideReason?: string;
};

type ForceFailRefundPayload = {
  reason: string;
  acknowledged: boolean;
};

type FrequentReturnRelaxationPayload = {
  reason: string;
  acknowledged: boolean;
};

type MtoMtcCodRtoAdvanceWaiverPayload = {
  reason: string;
  acknowledged: boolean;
  expectedPackageVersion: number;
  expectedLineItemVersion: number;
  expectedRefundVersion: number;
};

class RefundAPIClient extends CrudApiClient {
  constructor() {
    super(GATEWAY_API_URL + '/order/refund');
  }

  private resolveRefundRequestId(meta: RefundActionMeta): number {
    const refundRequestId = meta?.refundRequestId;
    if (!refundRequestId || !Number.isFinite(refundRequestId)) {
      throw new Error('refundRequestId is required for refund custom actions');
    }
    return refundRequestId;
  }

  private async putByRefundId<TPayload>(
    pathSuffix: string,
    payload: TPayload,
    meta: RefundActionMeta | undefined,
  ): Promise<any> {
    const refundRequestId = this.resolveRefundRequestId(meta ?? {});
    const url = `${this.baseURL}/${refundRequestId}${pathSuffix}`;
    const response = await axiosInstance.put(url, payload);
    if (response.data?.success === false || response.data?.status?.type === 'ERROR') {
      throw new Error(response.data?.status?.message || 'Refund action failed.');
    }
    return this.transformNode(response.data?.data);
  }

  async updateRefundModeDetails(
    _sorters: Array<CrudSort>,
    _filters: CrudFilters,
    payload: UpdateRefundModePayload,
    _query: unknown,
    _headers: unknown,
    meta: RefundActionMeta | undefined,
  ): Promise<any> {
    return this.putByRefundId('/resolve-onhold', payload, meta);
  }

  async adjustRefundAmount(
    _sorters: Array<CrudSort>,
    _filters: CrudFilters,
    payload: AdjustRefundAmountPayload,
    _query: unknown,
    _headers: unknown,
    meta: RefundActionMeta | undefined,
  ): Promise<any> {
    return this.putByRefundId('/onhold/adjust-amount', payload, meta);
  }

  async shelveRefund(
    _sorters: Array<CrudSort>,
    _filters: CrudFilters,
    payload: ShelveRefundPayload,
    _query: unknown,
    _headers: unknown,
    meta: RefundActionMeta | undefined,
  ): Promise<any> {
    return this.putByRefundId('/onhold/shelve', payload, meta);
  }

  async recoverRefund(
    _sorters: Array<CrudSort>,
    _filters: CrudFilters,
    payload: RecoverRefundPayload,
    _query: unknown,
    _headers: unknown,
    meta: RefundActionMeta | undefined,
  ): Promise<any> {
    return this.putByRefundId('/onhold/recover', payload, meta);
  }

  async retryRefund(
    _sorters: Array<CrudSort>,
    _filters: CrudFilters,
    payload: RetryRefundPayload,
    _query: unknown,
    _headers: unknown,
    meta: RefundActionMeta | undefined,
  ): Promise<any> {
    return this.putByRefundId('/retry', payload, meta);
  }

  async forceFailForRetry(
    _sorters: Array<CrudSort>,
    _filters: CrudFilters,
    payload: ForceFailRefundPayload,
    _query: unknown,
    _headers: unknown,
    meta: RefundActionMeta | undefined,
  ): Promise<any> {
    return this.putByRefundId('/force-fail-for-retry', payload, meta);
  }

  async getFrequentReturnRefundRelaxationQuote(
    _sorters: Array<CrudSort>,
    _filters: CrudFilters,
    _payload: unknown,
    _query: unknown,
    _headers: unknown,
    meta: RefundActionMeta | undefined,
  ): Promise<any> {
    const refundRequestId = this.resolveRefundRequestId(meta ?? {});
    const url = `${this.baseURL}/${refundRequestId}/frequent-return-refund-relaxation/quote`;
    const response = await axiosInstance.get(url);
    if (response.data?.success === false || response.data?.status?.type === 'ERROR') {
      throw new Error(
        response.data?.status?.message || 'Unable to fetch frequent-return refund quote.',
      );
    }
    return this.transformNode(response.data?.data);
  }

  async relaxFrequentReturnRefundRestriction(
    _sorters: Array<CrudSort>,
    _filters: CrudFilters,
    payload: FrequentReturnRelaxationPayload,
    _query: unknown,
    _headers: unknown,
    meta: RefundActionMeta | undefined,
  ): Promise<any> {
    return this.putByRefundId('/frequent-return-refund-relaxation', payload, meta);
  }

  async applyMtoMtcCodRtoAdvanceWaiver(
    _sorters: Array<CrudSort>,
    _filters: CrudFilters,
    payload: MtoMtcCodRtoAdvanceWaiverPayload,
    _query: unknown,
    _headers: unknown,
    meta: RefundActionMeta | undefined,
  ): Promise<any> {
    return this.putByRefundId('/mto-mtc-cod-rto-advance-waiver', payload, meta);
  }

  async convertStoreCreditRefund(
    _sorters: Array<CrudSort>,
    _filters: CrudFilters,
    payload: ConvertStoreCreditRefundPayload,
    _query: unknown,
    _headers: unknown,
    meta: RefundActionMeta | undefined,
  ): Promise<any> {
    return this.putByRefundId('/store-credit-conversion', payload, meta);
  }
}

export default RefundAPIClient;
